import { InfoOutlined } from '@mui/icons-material';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { JobType } from '@thro-types/jobmarket';

import { subDays } from 'date-fns';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { useJobPricesQuery } from 'api/hooks/jobmarket/jobprices';
import { useJobTypesQuery } from 'api/hooks/jobmarket/jobtypes';
import StyledDropzone, { DropzoneContainer } from 'app/components/Dropzone';
import { FormError } from 'app/components/FormError/FormError';
import InfoBox from 'app/components/InfoBox';
import FacultyBox from 'app/containers/Jobmarket/CompanyDetail/components/FacultyBox';
import { translations } from 'locales/i18n';
import { Course, Faculty, JobOffering } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import '../styles/ManageOffer.scss';

interface Props {
  offer: JobOffering;
  faculties: Faculty[];
  selectedCourses: Course[];
  editMode?: boolean;
  newOffer?: boolean;
  companyDataComplete?: boolean;
  isEmployee?: boolean;
}

const OfferCourses = ({
  offer,
  faculties,
  selectedCourses,
  editMode,
  newOffer,
  companyDataComplete,
  isEmployee
}: Props) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuthContext();
  const aclHelper = new ACLHelper(currentUser);
  const formik = useFormikContext<JobOffering>();
  const { data: typeData } = useJobTypesQuery();

  const paidTypes = typeData.filter(jt => jt.paid)?.map(jt => jt.id);
  const { data: jobPriceData } = useJobPricesQuery();

  const disabledDay = subDays(new Date(), 3);

  function shouldBeDisabled(jobType: JobType) {
    if (aclHelper.isJobmarketAdmin()) {
      return false;
    }
    if (jobType.name === 'Studentische Hilfskraft' && !aclHelper.isUniversityCompanyUser()) {
      return true;
    }
    if (newOffer) {
      return jobType.paid && (!companyDataComplete || isEmployee);
    } else {
      //The JobOffer exists for more than 3 days
      if (new Date(offer.createdAt) <= disabledDay) {
        // If the jobType is paid we are not allowed to switch anymore
        if (offer.jobType.paid) {
          return true;
        }
        // If the jobType is free we can switch between free jobtypes
        else {
          return jobType.paid;
        }
      }
      // If the JobOffer was created less than 3 days ago, we can switch between all JobTypes
      else {
        return jobType.paid && (!companyDataComplete || isEmployee);
      }
    }
  }

  const shouldJobTypeSelectBeDisabled = !typeData.find(jt => !shouldBeDisabled(jt));

  const jobTypeOptions = () => {
    return typeData.map(jt => {
      const disabled = shouldBeDisabled(jt);
      // This looks absolutely stupid, but we need this is a workaround beacuse of a mui bug
      if (disabled && jt.name === 'Studentische Hilfskraft') {
        return (
          <Tooltip
            title={t(
              translations.pages.jobOffers.changeJobType.disabledStudentAssistantJobTypeTooltip()
            )}
            className="MenuItem"
          >
            <MenuItem key={jt.id} value={jt.id} disabled={true}>
              {jt.name}
              {jt.paid && ' *'}
            </MenuItem>
          </Tooltip>
        );
      }
      return disabled ? (
        <Tooltip
          title={t(translations.pages.jobOffers.changeJobType.disabledJobTypeTooltip())}
          className="MenuItem"
        >
          <MenuItem key={jt.id} value={jt.id} disabled={true}>
            {jt.name}
            {jt.paid && ' *'}
          </MenuItem>
        </Tooltip>
      ) : (
        <MenuItem key={jt.id} value={jt.id}>
          {jt.name}
          {jt.paid && ' *'}
        </MenuItem>
      );
    });
  };

  const getJobTypeNameFromId = (id: number) => {
    return typeData.find(jobType => jobType.id === id)?.name;
  };

  const readOnlyFields = () => {
    return (
      <Grid item xs={12} className="section">
        <Grid container>
          <Grid container>
            {offer.attachement && (
              <Grid item xs={12} className="section__row">
                <Grid container className="section__header">
                  {t(translations.pages.jobOffers.downloadAttachement())}
                </Grid>
                <Grid container>
                  <DropzoneContainer>{offer.attachement}</DropzoneContainer>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container className="section__header">
                {t(translations.common.type())}
              </Grid>
              <Grid container className="manage-job-offer-detail__body__type">
                <Typography>{getJobTypeNameFromId(offer.jobTypeId)}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container className="section__header">
            {t(translations.common.courses())}
          </Grid>
          {faculties?.map(f => (
            <FacultyBox
              key={f.id}
              editMode={editMode}
              faculty={f}
              selectedCourses={selectedCourses}
            />
          ))}
        </Grid>
      </Grid>
    );
  };

  if (!editMode || formik === undefined) {
    return readOnlyFields();
  }
  const { errors, setFieldValue, values } = formik;

  return (
    <Grid container item xs={12} className="section">
      <Grid container>
        <Grid container>
          <Grid item xs={12} className="section__row">
            <Grid container className="section__header">
              {t(translations.pages.jobOffers.downloadAttachement())}
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <StyledDropzone
                  name="attachement"
                  setFieldValue={setFieldValue}
                  value={values.attachement}
                  errors={errors}
                  accept={{
                    'application/msword': ['.doc', '.dot'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                      '.docx',
                      '.dotx'
                    ],
                    'application/pdf': ['.pdf'],
                    'application/zip': ['.zip']
                  }}
                  placeholder={t(translations.pages.manageJobOffers.attachementUploadPlaceholder())}
                />
              </Grid>
              <Grid item>
                <FormHelperText>
                  {t(translations.pages.manageJobOffers.attachementUploadInfo(), {
                    size: '128 MB'
                  })}
                </FormHelperText>
              </Grid>
            </Grid>
          </Grid>
          {newOffer && (
            <Grid item xs={12} className="section__row">
              <Grid container className="section__header">
                {t(translations.pages.manageJobOffers.displayPeriod())}
              </Grid>
              <Grid container>
                <Field name="periodStart">
                  {({ form, field }) => (
                    <DatePicker
                      className="manage-job-offer-detail__body__periods__start-date"
                      disablePast
                      renderInput={props => (
                        <TextField
                          {...props}
                          variant="standard"
                          required
                          label={t(translations.pages.manageJobOffers.periodStart())}
                        />
                      )}
                      inputFormat="dd.MM.yyyy"
                      value={form.values.periodStart}
                      onChange={e => {
                        form.setFieldValue('periodStart', e);
                      }}
                    />
                  )}
                </Field>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} className="section__row">
            <Grid container className="section__header" gap={1} alignItems="center">
              {t(translations.pages.manageJobOffers.externalLink())}
              <Tooltip title={t(translations.pages.manageJobOffers.externalLinkTooltip())}>
                <InfoOutlined />
              </Tooltip>
            </Grid>
            <Grid item xs>
              <Field
                as={TextField}
                name="externalLink"
                error={!!errors.externalLink}
                helperText={errors.externalLink}
                label={t(translations.pages.manageJobOffers.externalLink())}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className="section__row">
            <Grid container className="section__header">
              {t(translations.common.type())}
            </Grid>
            <Grid container className="manage-job-offer-detail__body__type">
              <Field name="jobTypeId">
                {({ form, field }) => (
                  <FormControl required error={form.errors.jobTypeId}>
                    <Tooltip
                      title={
                        shouldJobTypeSelectBeDisabled
                          ? t(
                              translations.pages.jobOffers.changeJobType.cantChangeFromPaidTooltip()
                            )
                          : ''
                      }
                    >
                      <Select
                        value={form.values.jobTypeId}
                        onChange={e => {
                          setFieldValue('jobTypeId', e.target.value);
                          setFieldValue('jobPriceId', '');
                        }}
                        disabled={shouldJobTypeSelectBeDisabled}
                      >
                        {jobTypeOptions()}
                      </Select>
                    </Tooltip>
                    {form.errors.jobTypeId && (
                      <FormHelperText>{form.errors.jobTypeId}</FormHelperText>
                    )}
                  </FormControl>
                )}
              </Field>
              <FormHelperText>
                {t(translations.pages.manageJobOffers.paidCategoriesInformation())}
              </FormHelperText>
            </Grid>
          </Grid>
          {offer?.jobType?.paid === false &&
            typeData?.find(pt => pt.id === values.jobTypeId)?.paid && (
              <Grid item xs className="section__row">
                <InfoBox
                  type="warning"
                  title={t(
                    translations.pages.jobOffers.changeJobType.changeFromFreeToPaidJobTypeInfoBoxTitle()
                  )}
                  expandable={false}
                >
                  {t(
                    translations.pages.jobOffers.changeJobType.changeFromFreeToPaidJobTypeInfoBox()
                  )}
                </InfoBox>
              </Grid>
            )}
          <Grid item xs={12} className="section__row">
            {jobPriceData && values.jobTypeId && (
              <Field name="jobPriceId">
                {({ form, field }) => (
                  <FormControl required error={form.errors.jobPriceId} component="fieldset">
                    <FormLabel component="legend">
                      {t(translations.pages.manageJobOffers.priceCategories())}
                    </FormLabel>
                    <RadioGroup
                      value={form.values.jobPriceId}
                      onChange={field.onChange(field.name)}
                    >
                      {jobPriceData
                        .filter(price =>
                          paidTypes.includes(values.jobTypeId) ? price.price > 0 : price.price === 0
                        )
                        ?.map(price => {
                          return (
                            <FormControlLabel
                              key={price.id}
                              value={price.id}
                              control={<Radio />}
                              label={`${price.name} (${t(
                                translations.pages.manageJobOffers.factors[
                                  price.duration < 2 ? price.factor : `${price.factor}_plural`
                                ](),
                                { duration: price.duration }
                              )}, ${price.price}€)`}
                            />
                          );
                        })}
                    </RadioGroup>
                    {form.errors.jobPriceId && (
                      <FormHelperText>{form.errors.jobPriceId}</FormHelperText>
                    )}
                  </FormControl>
                )}
              </Field>
            )}
          </Grid>
        </Grid>
        <Grid container className="section__header">
          {t(translations.common.courses())}
        </Grid>
        {errors.courses && (
          <Grid container className="section__error">
            <FormError message={errors.courses.toString()} />
          </Grid>
        )}
        <FieldArray name="courses">
          {({ form, ...helpers }) =>
            faculties?.map(f => (
              <FacultyBox
                key={f.id}
                editMode={editMode}
                faculty={f}
                selectedCourses={form.values.courses}
                arrayHelpers={helpers}
              />
            ))
          }
        </FieldArray>
      </Grid>
    </Grid>
  );
};

export default OfferCourses;
